import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  AnyObject,
  AuthCache,
  CardDataSetConfig,
  CardStructureConfig,
  CommonDataSetAction,
  ElementDataSet,
  FormFieldConfig, ItemAttrListDataSet,
  OnCommonAction,
  PaginateDto,
  ValueType
} from '@mri/types';
import { AuthResourceService, GenericResourceService } from '@mri/app/resources';
import { finalize, mergeMap, of, Subscription, tap } from 'rxjs';
import { Socket, SocketIoConfig } from 'ngx-socket-io';
import { environment } from '@mri/environment';
import { MatDrawer } from '@angular/material/sidenav';

@Component({
  selector: 'app-card-data-set',
  templateUrl: './card-data-set.component.html',
  styleUrl: './card-data-set.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CardDataSetComponent implements OnInit {
  protected readonly Array = Array;

  private _subscriptions = new Subscription();
  private _disableSelectOnly?: boolean;
  private _gatewayUrl?: string;
  private _hasSocket?: boolean;
  private _socket?: Socket;

  structure!: CardStructureConfig<AnyObject>;
  filterFields?: FormFieldConfig[];
  actions!: CommonDataSetAction[];
  countFindElements!: number;
  isLoading: boolean;

  @ViewChild('drawerFilter') drawerFilter: MatDrawer;

  @Input() filterValueRecord!: Record<string, ValueType | ValueType[]>;
  @Input() joints: Record<string, Record<string, AnyObject>> = {};
  @Input() marks: Record<string, Record<string, boolean>> = {};
  @Input() selectedIds: Set<string> = new Set();
  @Input() sources: AnyObject[];
  @Input() width: number = 250;
  @Input() size: number = 50;

  @Output() filterValueRecordChange = new EventEmitter<Record<string, ValueType | ValueType[]>>();
  @Output() actionListener = new EventEmitter<OnCommonAction>();
  @Output() selectedElementsListener = new EventEmitter<Array<AnyObject>>();
  @Output() visibleElementListener = new EventEmitter<Array<AnyObject>>();
  @Output() loadedElementsListener = new EventEmitter<Array<AnyObject>>();
  @Output() hiddenElementListener = new EventEmitter<Array<AnyObject>>();
  @Output() doubleElementsListener = new EventEmitter<AnyObject>();
  @Output() receiverSocketElement = new EventEmitter<AnyObject>();

  get actionsInRow() {
    return this.actions.filter(a => a.inRow);
  }

  get elements(): Array<AnyObject & ElementDataSet> | undefined {
    if (!this.sources) return undefined;

    return this.sources;
  }

  get filterCount() {
    // cada valor de filterFields es un array, cuenta la cantidad de elementos en cada array y los suma
    // si filterFields es undefined, retorna 0
    return Object.keys(this.filterValueRecord || {})?.reduce((acc, field) => {
      const value = (this.filterValueRecord || {})[field] || null;
      if (!value) return acc;
      if (Array.isArray(value)) return acc + value.length;
      return acc + 1;
    }, 0) || 0;
  }

  get selectedElements(): Array<AnyObject & ElementDataSet> {
    return this.sources.filter(element => this.selectedIds.has(element._id));
  }

  @Input({ required: true })
  set config(config: CardDataSetConfig) {
    const {
      structure, actions,
      gatewayUrl, disableSelectOnly,
      hasSocket, filter
    } = config;
    this.structure = structure;
    this.actions = actions;
    this._gatewayUrl = gatewayUrl;
    this._disableSelectOnly = disableSelectOnly;
    this._hasSocket = hasSocket;
    this.filterFields = filter;
  }

  constructor(
    private _genericSrcSrv: GenericResourceService,
    private _changeDetector: ChangeDetectorRef,
    private _authSrv: AuthResourceService
  ) {
  }

  ngOnInit(): void {
    this._subscriptions.add(
      this._authSrv.auth$
        .pipe(
          tap(auth => auth && this._connectSocket(auth)),
          tap(() => this.reload())
        ).subscribe()
    );
  }

  getExtraData(element: AnyObject): ItemAttrListDataSet {
    return {
      element,
      selectedElements: [...this.selectedElements],
      elements: this.sources,
      marks: this.marks,
      joint: this.joints,
      totalCount: this.countFindElements,
      filterValueRecord: this.filterValueRecord,
    }
  }

  reload(paginate: PaginateDto = { page: 1, size: this.size }) {
    if (!this._gatewayUrl) return;

    const url = this._gatewayUrl;
    const { page } = paginate;
    this.isLoading = true;

    const filter = this.filterValueRecord;

    of(null)
      .pipe(
        mergeMap(() => this._genericSrcSrv.countElements(url, { filter })),
        tap(response => this.countFindElements = (response.count) || 0),

        mergeMap(() => this._genericSrcSrv.listElements(url, { paginate: paginate, filter })),
        tap(({ objects }) =>
          this.sources = page === 1 ? objects : [...this.sources, ...objects]
        ),
        tap(() => this.loadedElementsListener.emit(this.sources)),

        tap(() => this._socket?.emit('subscribe', (this.elements || []).map(e => e._id))),
        finalize(() => this.isLoading = false)
      ).subscribe();

  }

  pressAction(action: string, elements?: Array<AnyObject>) {
    elements = elements || this.selectedElements;
    this.actionListener.next({ action, elements });
  }

  toggleSelect(element: AnyObject) {
    if (this.selectedIds.has(element._id)) {
      this.selectedIds.delete(element._id);
      this.selectedElementsListener.emit(this.selectedElements);

      return;
    }

    this.selectedIds.add(element._id);
    this.selectedElementsListener.emit(this.selectedElements);

  }

  addSelect(element: AnyObject) {
    if (this.selectedIds.has(element._id)) return;

    this.selectedIds.add(element._id);
    this.selectedElementsListener.emit(this.selectedElements);

  }

  onlySelect(element: AnyObject) {
    if (!this._disableSelectOnly) {
      this.selectedIds = new Set([element._id]);
    } else {
      this.addSelect(element);
    }
    this.selectedElementsListener.emit(this.selectedElements);
  }


  onVisible(element: AnyObject, isVisible: boolean, index: number) {
    if (isVisible) return this.onShow(element, index);
    this.onHide(element);
  }

  onShow(element: AnyObject & ElementDataSet, index: number) {
    this.visibleElementListener.emit([element]);
    if (this.isLoading) return;

    element.$show = true;
    const currentCount = this.sources.length;

    if (!(currentCount - 10 < index)) return;
    if (this.countFindElements <= currentCount) return;

    const page = parseInt((currentCount / this.size).toString()) + 1;
    this.reload({ size: this.size, page });
  }

  onHide(element: AnyObject) {
    this.hiddenElementListener.emit([element]);
  }

  onDoubleClick(element: AnyObject): void {
    this.doubleElementsListener.emit(element);
  }

  updateRender() {
    this._changeDetector.markForCheck();
  }

  setMark(element: AnyObject, key: string, value: boolean) {
    const id = element._id;
    const mark = this.marks[id] || {};

    mark[key] = value;
    if (!mark[key]) delete mark[key];

    this.marks[id] = mark;
    element.$update = new Date().toString();
    this._changeDetector.markForCheck();

    setTimeout(() => {
      element.$update = null;
      this._changeDetector.markForCheck();
    }, 50);

  }

  toggleMark(element: AnyObject, key: string) {
    const id = element._id;
    const mark = this.marks[id] || {};

    this.setMark(element, key, !mark[key]);
  }

  onChangeFilterValues(values: Record<string, ValueType | ValueType[]>) {
    this.filterValueRecord = values;
    this.filterValueRecordChange.emit(values);

    this.drawerFilter.close().then();
    this.reload({ page: 1, size: this.size });
  }


  private _connectSocket(auth: AuthCache) {
    if (!this._gatewayUrl) return;
    if (!this._hasSocket) return;

    const url = `${environment.apiUrl}/${this._gatewayUrl}`;

    const config: SocketIoConfig = { url, options: { query: { token: auth.session.token } } };

    this._socket = new Socket(config);
    this._socket.connect();

    if (this.elements) {
      this._socket?.emit('subscribe', (this.elements || []).map(e => e._id));
    }

    this._subscriptions.add(
      this._socket.fromEvent('element')
        .pipe(
          tap((element: AnyObject) => {
            const index = this.sources.findIndex(e => e._id === element._id);
            this.receiverSocketElement.emit(element);
            if (index === -1) return;
            this.sources[index] = element;
            this.updateRender();
          })
        ).subscribe()
    );

    this._subscriptions.add(
      this._socket.fromEvent('connect')
        .pipe(
          tap(() => {
            console.log('Reconected');
            this._socket?.emit('subscribe', (this.elements || []).map(e => e._id));
          })
        ).subscribe()
    );
  }
}
